import React from "react";
import CountWidget from "Pages/Dashboard/dashboard/DashboardV2/Widgets/CountWidget";
import RateWidget from "Pages/Dashboard/dashboard/DashboardV2/Widgets/RateWidget";
import { formatDelta } from "./formatMetric";

/**
 * One headline number in the chat — a minimized version of the dashboard's own
 * widgets rather than a second implementation.
 *
 * Percentages render through RateWidget and counts through CountWidget, the
 * same components the reports pages use, via their `label` and `size` props.
 * So a number shown in the copilot formats, rounds and badges exactly as the
 * same number does on the dashboard, and a fix to either lands in both.
 *
 * What remains here is only what the chat adds on top: the delta wording
 * ("+12.4% vs last month" instead of the dashboard's fixed "vs prev. period")
 * and the sentiment split — a rising bounce rate is up AND bad. Both are
 * passed through on the trend object.
 */
const StatTile = ({
  label,
  value,
  unit = "",
  delta,
  footnote,
  sparkline,
  size = "lg"
}) => {
  const isRate = unit === "%";

  const trend =
    delta && delta.value != null
      ? {
          percent: delta.value,
          direction: delta.direction || "flat",
          sentiment: delta.sentiment || "neutral",
          text: `${formatDelta(delta.value, delta.unit || unit)}${
            delta.label ? ` ${delta.label}` : ""
          }`
        }
      : null;

  // Sparkline points arrive as bare numbers; both widgets take [{label,value}].
  // Passing chartData directly skips the widgets' dateFilter bucketing, which
  // the chat has no dates to bucket by.
  const chartData =
    Array.isArray(sparkline) && sparkline.length > 1
      ? sparkline.map((point, index) => ({
          label: String(index + 1),
          value: Number(point) || 0
        }))
      : [];

  const shared = {
    label,
    size,
    trend,
    metricLabel: label,
    // KPI hides the chart entirely, which is what a tile with no sparkline is.
    visualization: chartData.length ? "AREA" : "KPI"
  };

  return (
    <div className={`cpStatTile cpStatTile--${size}`}>
      {isRate ? (
        <RateWidget
          {...shared}
          currentRate={value == null ? null : Number(value)}
          chartData={chartData}
          bottomStat={footnote}
        />
      ) : (
        <>
          <CountWidget
            {...shared}
            value={value == null ? null : Number(value)}
            chartData={chartData}
            unit={unit === "currency" ? "" : unit}
          />
          {footnote && <div className="cpStatTile__footnote">{footnote}</div>}
        </>
      )}
    </div>
  );
};

export default StatTile;
